class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.boundUpdateResultsPosition = this.updateResultsPosition.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
    this.boundOnKeyup = this.onKeyup.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnFocus = this.onFocus.bind(this);
    this.boundOnFocusOut = this.onCordingsFocusOut.bind(this);

    if (!this.cordingsMode) {
      this.setupEventListeners();
    }
  }

  connectedCallback() {
    if (!this.cordingsMode) return;

    this.initCordingsMode();
    this.setupEventListeners();
    this.setupCordingsListeners();
  }

  disconnectedCallback() {
    this.msearchObserver?.disconnect();
    this.compactObserver?.disconnect();
    window.clearTimeout(this._inputDebounce);
    document.body.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.boundUpdateResultsPosition);
    window.removeEventListener('scroll', this.boundUpdateResultsPosition);
  }

  setupCordingsListeners() {
    if (this.msearch) {
      this.msearchObserver = new MutationObserver(() => {
        if (!this.msearch.classList.contains('is-open') && this.isMobile()) {
          this.close(this.isOpen);
        } else if (this.isOpen) {
          this.updateResultsPosition();
          this.syncBodyClasses();
        }
      });
      this.msearchObserver.observe(this.msearch, { attributes: true, attributeFilter: ['class'] });
    }

    const compact = this.header?.querySelector('[data-compact]');
    if (compact) {
      this.compactObserver = new MutationObserver(() => {
        if (this.isOpen) this.updateResultsPosition();
      });
      this.compactObserver.observe(compact, { attributes: true, attributeFilter: ['class'] });
    }

    this.predictiveSearchResults?.addEventListener('mousedown', (event) => {
      event.preventDefault();
    });
  }

  setupEventListeners() {
    if (!this.cordingsMode) {
      this.input?.form?.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input?.addEventListener('focus', this.boundOnFocus);
    } else {
      this.inputs?.forEach((input) => {
        input.addEventListener('focus', this.boundOnFocus);
        input.addEventListener('focusout', this.boundOnFocusOut);
        input.addEventListener('keyup', this.boundOnKeyup);
        input.addEventListener('keydown', this.boundOnKeydown);
      });
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    if (!this.cordingsMode) {
      this.addEventListener('keyup', this.boundOnKeyup);
      this.addEventListener('keydown', this.boundOnKeydown);
    }
    document.addEventListener('keydown', this.onDocumentKeydown.bind(this));
    document.body.addEventListener('click', this.onBodyClick);
  }

  syncBodyClasses() {
    const anyBarOpen = document.querySelector('predictive-search[data-cordings]')
      ? document.querySelector('.cds-msearch.is-open')
      : document.querySelector('predictive-search[mobile-bar-open], search-form[mobile-bar-open]');
    const anyResultsOpen = document.querySelector('predictive-search[open]');

    document.body.classList.toggle('predictive-search-bar-open', Boolean(anyBarOpen));
    document.body.classList.toggle('predictive-search-open', Boolean(anyBarOpen || anyResultsOpen));
  }

  onMobileToggleClose() {
    this.close(true);
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  onChange() {
    if (this.isResetting) return;

    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.closePredictiveOnly(this.isMobile() && (this.cordingsMode ? this.isMsearchOpen() : this.hasAttribute('mobile-bar-open')));
      return;
    }

    if (this.isMobile() && !this.cordingsMode) {
      this.openMobileBar();
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a, [aria-selected="true"].predictive-search__link')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    event.preventDefault();
    if (!this.shouldResetForm()) return;

    this.isResetting = true;
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.clearSearchQueryFromUrl();

    const mobileOpen = this.cordingsMode ? this.isMsearchOpen() : this.hasAttribute('mobile-bar-open');

    if (this.isMobile() && mobileOpen) {
      this.closePredictiveOnly(true, true);
    } else {
      this.closeResults(true);
    }

    this.input?.focus();
    window.setTimeout(() => {
      this.isResetting = false;
    }, 150);
  }

  onFocus(event) {
    if (this.cordingsMode) this.setActiveInput(event.currentTarget);

    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.isMobile() && !this.cordingsMode) {
      this.openMobileBar();
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onCordingsFocusOut() {
    if (this.isResetting) return;

    // Desktop: dismiss via body click only — avoids closing before debounced input runs.
    if (!this.isMobile()) return;

    window.setTimeout(() => {
      if (this.isResetting) return;
      if (this.contains(document.activeElement)) return;
      if (this.inputs?.some((input) => input === document.activeElement)) return;
      if (!this.isMsearchOpen()) return;

      this.closeResults(false);
    }, 150);
  }

  onFocusOut() {
    if (this.cordingsMode) return;
    if (this.isResetting) return;

    setTimeout(() => {
      if (this.isResetting) return;
      if (this.contains(document.activeElement)) return;

      if (this.isMobile() && this.hasAttribute('mobile-bar-open')) {
        this.closePredictiveOnly(true);
        return;
      }

      this.close(false);
    });
  }

  onBodyClick(event) {
    const mobileActive = this.cordingsMode ? this.isMsearchOpen() : this.hasAttribute('mobile-bar-open');
    if (!this.isOpen && !mobileActive) return;
    if (this.contains(event.target)) return;
    if (this.cordingsMode && this.header?.contains(event.target)) return;
    this.close(this.isMobile());
  }

  onDocumentKeydown(event) {
    if (event.code !== 'Escape') return;

    const mobileActive = this.cordingsMode ? this.isMsearchOpen() : this.hasAttribute('mobile-bar-open');
    if (!this.isOpen && !mobileActive) return;

    this.close(true);
    if (!this.isMobile()) this.input?.focus();
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      const mobileOpen = this.cordingsMode ? this.isMsearchOpen() : this.hasAttribute('mobile-bar-open');
      this.closePredictiveOnly(this.isMobile() && mobileOpen);
    }

    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'Enter') {
      event.preventDefault();
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, a.predictive-search__link')).filter(
      (element) => element.offsetParent !== null
    );

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i++) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }

    let activeElementIndex = selectedElementIndex;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input?.setAttribute('aria-activedescendant', activeElement.id);

    const status = this.statusElement || this.querySelector('.predictive-search-status');
    if (status) status.textContent = '';
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, [aria-selected="true"].predictive-search__link');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=5&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.updateResultsPosition();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.updateSeeAllCount(this.searchTerm);
    this.open();
  }

  updateSeeAllCount(searchTerm) {
    const link = this.predictiveSearchResults.querySelector('[data-see-all-template]');
    if (!link || !searchTerm || !window.SearchUtils) return;

    const template = this.decodeDataAttribute(link.getAttribute('data-see-all-template'));
    const fallback = this.decodeDataAttribute(link.getAttribute('data-see-all-fallback'));
    const predictiveLimit = parseInt(link.dataset.predictiveLimit || '5', 10);
    const textEl = link.querySelector('[data-see-all-text]');
    if (!textEl) return;

    const quotedQuery = `'${searchTerm}'`;

    SearchUtils.fetchProductTotal(searchTerm, link.href).then((total) => {
      if (!total) return;

      const resultsCount = Math.max(total - predictiveLimit, 0);

      if (resultsCount <= 0) {
        if (total > 0 && total <= predictiveLimit) {
          link.closest('.predictive-search__all')?.remove();
        }
        return;
      }

      textEl.textContent = template
        .replace('__COUNT__', String(resultsCount))
        .replace('__QUERY__', quotedQuery);
    }).catch(() => {
      if (fallback) textEl.textContent = fallback.replace('__QUERY__', quotedQuery);
    });
  }

  decodeDataAttribute(value) {
    if (!value) return '';
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getHeaderAnchorBottom() {
    if (!this.header) {
      return document.querySelector('.section-header')?.getBoundingClientRect().bottom ?? 0;
    }

    if (this.isMobile()) {
      if (this.isMsearchOpen() && this.msearch) {
        return this.msearch.getBoundingClientRect().bottom;
      }
      const bar = this.header.querySelector('.cds-header__bar');
      return bar?.getBoundingClientRect().bottom ?? 0;
    }

    const compact = this.header.querySelector('[data-compact]');
    if (compact?.classList.contains('is-visible')) {
      return compact.getBoundingClientRect().bottom;
    }

    const bar = this.header.querySelector('.cds-header__bar');
    return bar?.getBoundingClientRect().bottom ?? 0;
  }

  updateResultsPosition() {
    const anchorBottom = this.getHeaderAnchorBottom();

    document.documentElement.style.setProperty('--header-height', `${anchorBottom}px`);
    document.documentElement.style.setProperty('--header-search-bar-height', '0px');

    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.style.top = `${anchorBottom}px`;
    this.predictiveSearchResults.style.maxHeight = `${window.innerHeight - anchorBottom}px`;
  }

  open() {
    if (this.isMobile() && !this.cordingsMode) {
      this.openMobileBar();
    }

    this.updateResultsPosition();
    this.setAttribute('open', true);
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.syncBodyClasses();

    window.addEventListener('resize', this.boundUpdateResultsPosition);
    window.addEventListener('scroll', this.boundUpdateResultsPosition, { passive: true });
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closePredictiveOnly(keepMobileBarOpen = false, clearSearchTerm = false) {
    this.closeResults(clearSearchTerm, keepMobileBarOpen);
    this.isOpen = false;
  }

  clearPredictiveResultsMarkup() {
    this.predictiveSearchResults?.querySelector('#predictive-search-results')?.remove();
  }

  closeResults(clearSearchTerm = false, keepMobileBarOpen = false) {
    if (clearSearchTerm) {
      if (this.cordingsMode) {
        this.inputs?.forEach((input) => {
          input.value = '';
        });
      } else if (this.input) {
        this.input.value = '';
      }
      this.searchTerm = '';
      this.removeAttribute('results');
      this.toggleResetButton();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input?.setAttribute('aria-activedescendant', '');
    this.input?.setAttribute('aria-expanded', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.predictiveSearchResults?.removeAttribute('style');
    this.clearPredictiveResultsMarkup();

    if (this.isMobile() && !keepMobileBarOpen) {
      this.closeMobileBar(clearSearchTerm);
    }

    window.removeEventListener('resize', this.boundUpdateResultsPosition);
    window.removeEventListener('scroll', this.boundUpdateResultsPosition);
    this.syncBodyClasses();
  }
}

customElements.define('predictive-search', PredictiveSearch);
